import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Navigate, Link, useSearchParams } from 'react-router-dom';
import { useRecentEntries, useEntriesExposure, useHotCompositions } from '../api/hooks';
import { maxAbsLiabilityFromRows, liabilityCellClasses } from './marketsLineBreakdown';
import {
  entryPromoRowClass,
  formatReplicationBadge,
  getSharpColor,
  getSharpTextColor,
  sharpCellStyle,
} from './entryTickerUtils';
import './EntriesPage.css';

function formatTs(ts) {
  if (ts == null) return '—';
  const d = new Date(ts * 1000);
  return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
}

function formatEntryDate(ts) {
  if (ts == null) return '—';
  const d = new Date(Number(ts) * 1000);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString([], { dateStyle: 'short' });
}

function formatEntryStartTime(ts) {
  if (ts == null) return '—';
  const d = new Date(Number(ts) * 1000);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { timeStyle: 'short' });
}

function entryFixtureCompleted(row, nowMs) {
  const status = String(row?.fixture_status || '').trim().toLowerCase();
  if (['complete', 'completed', 'final', 'closed'].includes(status)) return true;
  const startMs = Number(row?.fixture_start_ts) * 1000;
  return Number.isFinite(startMs) && startMs <= nowMs;
}

function formatTimeToStart(row, nowMs) {
  const ts = row?.fixture_start_ts;
  if (ts == null) return '—';
  if (entryFixtureCompleted(row, nowMs)) return 'Closed';
  const startMs = Number(ts) * 1000;
  if (!Number.isFinite(startMs)) return '—';
  const diffMs = startMs - nowMs;
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function exposureRowMatchesClosedFilter(row, closedFilter, nowMs) {
  if (closedFilter === 'closed') return entryFixtureCompleted(row, nowMs);
  if (closedFilter === 'open') return !entryFixtureCompleted(row, nowMs);
  return true;
}

function formatFixture(row) {
  if (row.fixture_home_team && row.fixture_away_team) {
    return `${row.fixture_home_team} v ${row.fixture_away_team}`;
  }
  return row.fixture_start_time != null ? formatTs(row.fixture_start_time) : '—';
}

/** True when Dashboard can load this market (local ``market_quotes`` row); keys alone are not enough. */
function entryRowCanOpenDashboard(row) {
  const mk = row.market_key != null && String(row.market_key).trim() !== '';
  if (!mk) return false;
  return row.has_market_quote === true;
}

function exposureRowCanOpenDashboard(row) {
  return row?.market_key != null && String(row.market_key).trim() !== '';
}

function dashboardOrderBookHref(marketKey, line) {
  const params = new URLSearchParams({
    market: marketKey,
    tab: 'orderBook',
  });
  if (line != null && Number.isFinite(Number(line))) {
    params.set('line', String(Number(line)));
  }
  return `/dashboard?${params.toString()}`;
}

function entryStatusClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'applied') return 'entries-status-applied';
  if (s === 'skipped') return 'entries-status-skipped';
  if (s === 'pending') return 'entries-status-pending';
  if (s === 'unmanaged') return 'entries-status-unmanaged';
  if (s === 'canceled' || s === 'cancelled') return 'entries-status-canceled';
  return 'entries-status-other';
}

function parlayStatusClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'pending') return 'entries-parlay-pending';
  if (s === 'won' || s === 'lost' || s === 'void') return 'entries-parlay-settled';
  if (s === 'canceled' || s === 'cancelled') return 'entries-parlay-canceled';
  return 'entries-parlay-other';
}

/** Build full entry details for row tooltip (parlay_id, user_id, market_key, etc.). */
function getEntryRowTooltip(row) {
  const lines = [
    ['Market key', row.market_key],
    ['Parlay ID', row.parlay_id],
    ['User ID', row.user_id],
    ['Player', row.player_name],
    ['Market', row.market_id],
    ['Fixture', row.fixture_home_team && row.fixture_away_team ? `${row.fixture_home_team} v ${row.fixture_away_team}` : null],
    ['Outcome', row.outcome],
    ['Line', row.outcome_value],
    ['Fee', row.currency_amount],
    ['Sharp (raw)', row.sharp_score],
    ['Sharp (adj)', row.sharp_score_adjusted],
    ['Repl (reg)', row.replication_n_reg],
    ['Repl (hf)', row.replication_n_hf],
    ['Leg ×', row.payout_multiplier],
    ['Entry × (parlay)', row.entry_multiplier],
    ['Liab. contrib (leg)', row.liability_contrib],
    ['Prob', row.probability != null ? (row.probability * 100).toFixed(1) + '%' : null],
    ['Provider', row.provider],
    ['Sport', row.sport],
    ['Legs', row.num_legs],
    ['Promo', row.promo_type],
    ['Type', row.type],
    ['Line type', row.line_type],
    ['Profit boost %', row.profit_boost_percentage != null ? row.profit_boost_percentage*100 + '%' : null],
    ['Apply status', row.application_status],
    ['Apply reason', row.application_reason],
    ['Sleeper game id', row.entry_game_id],
    ['Parlay status', row.parlay_status],
    ['Game status', row.game_status],
  ];
  return lines
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');
}

/** Initial + Clear target; omit apply_status → API excludes unmanaged. */
const EMPTY_ENTRY_FILTERS = {
  player: '',
  market: '',
  user_id: '',
  outcome: '',
  sport: '',
  apply_status: '',
  provider: '',
  game_status: '',
  fixture_start_after: '',
  fixture_start_before: '',
  min_fee: '',
  min_sharp: '',
  parlay_id: '',
};

/** Values match ``true_odds.market_entries`` LIST partitions (see ``MARKET_ENTRIES_LIST_PARTITION_SPORTS`` in services/database/constants.py). */
const SPORT_FILTER_OPTIONS = [
  { value: '', label: 'Sport (all)' },
  { value: 'soccer', label: 'Soccer' },
  { value: 'nba', label: 'NBA' },
  { value: 'wnba', label: 'WNBA' },
  { value: 'mlb', label: 'MLB' },
  { value: 'cbb', label: 'CBB' },
  { value: 'esports', label: 'Esports' },
  { value: 'golf', label: 'Golf' },
  { value: 'football', label: 'NFL' },
  { value: 'ncaaf', label: 'NCAAF / CFB' },
  { value: 'nhl', label: 'NHL' },
];

/** Leg provider filter (exact match when set). */
const PROVIDER_FILTER_OPTIONS = [
  { value: '', label: 'Provider (all)' },
  { value: 'dl_trading', label: 'dl_trading' },
  { value: 'sleeper_odds', label: 'sleeper_odds' },
];

/**
 * `market_entries.game_status` — the producer's `line.game_status`, i.e. whether the leg was
 * taken pre-game or in-game, recorded at placement. Stored and matched verbatim, so these are
 * seeds rather than a closed set: `gameStatusFilterOptions` unions in any other value actually
 * present in the loaded rows, and a value the producer sends that we never listed still shows
 * up rather than being silently unfilterable. `none` selects rows with nothing stored (legs
 * from before the producer stamped the field) — not the same as pre-game.
 */
const GAME_STATUS_FILTER_SEEDS = [
  { value: 'pre_game', label: 'Pre-game' },
  { value: 'in_game', label: 'In-game' },
];
const GAME_STATUS_NONE_OPTION = { value: 'none', label: 'Unknown (not stored)' };

function gameStatusFilterOptions(rows, applied) {
  const seen = new Map(GAME_STATUS_FILTER_SEEDS.map((o) => [o.value, o]));
  (rows || []).forEach((r) => {
    const v = String(r.game_status || '').trim().toLowerCase();
    if (v && !seen.has(v)) seen.set(v, { value: v, label: v });
  });
  const current = String(applied || '').trim().toLowerCase();
  // Keep the applied value selectable even when filtering leaves no other rows carrying it.
  if (current && current !== GAME_STATUS_NONE_OPTION.value && !seen.has(current)) {
    seen.set(current, { value: current, label: current });
  }
  return [
    { value: '', label: 'Game status (all)' },
    ...Array.from(seen.values()),
    GAME_STATUS_NONE_OPTION,
  ];
}

function buildEntriesQueryParams(minutes, limit, sortBy, sortOrder, applied) {
  const afterTs = fixtureKickoffAfterTsForApi(applied.fixture_start_after);
  const beforeTs = fixtureKickoffBeforeTsForApi(applied.fixture_start_before);
  return {
    minutes: entriesMinutesForApi(minutes),
    limit,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...(applied.player && { player: applied.player.trim() }),
    ...(applied.market && { market: applied.market.trim() }),
    ...(applied.user_id && { user_id: applied.user_id.trim() }),
    ...(applied.outcome && { outcome: applied.outcome }),
    ...(applied.sport && { sport: applied.sport.trim() }),
    ...(applied.min_fee !== '' && applied.min_fee !== null && { min_fee: parseFloat(applied.min_fee) }),
    ...(applied.min_sharp !== '' && applied.min_sharp !== null && { min_sharp: parseFloat(applied.min_sharp) }),
    ...(applied.apply_status && { apply_status: applied.apply_status }),
    ...(applied.provider && { provider: applied.provider.trim() }),
    ...(applied.game_status && { game_status: applied.game_status.trim() }),
    ...(applied.parlay_id && { parlay_id: applied.parlay_id.trim() }),
    ...(afterTs != null && { fixture_start_after: afterTs }),
    ...(beforeTs != null && { fixture_start_before: beforeTs }),
  };
}

/** Shared filters + exposure sort (no ticker sort columns). */
function buildExposureQueryParams(minutes, limit, applied, expSortBy, expSortOrder) {
  const afterTs = fixtureKickoffAfterTsForApi(applied.fixture_start_after);
  const beforeTs = fixtureKickoffBeforeTsForApi(applied.fixture_start_before);
  return {
    minutes: entriesMinutesForApi(minutes),
    limit,
    sort_by: expSortBy,
    sort_order: expSortOrder,
    ...(applied.player && { player: applied.player.trim() }),
    ...(applied.market && { market: applied.market.trim() }),
    ...(applied.user_id && { user_id: applied.user_id.trim() }),
    ...(applied.outcome && { outcome: applied.outcome }),
    ...(applied.sport && { sport: applied.sport.trim() }),
    ...(applied.min_fee !== '' && applied.min_fee !== null && { min_fee: parseFloat(applied.min_fee) }),
    ...(applied.min_sharp !== '' && applied.min_sharp !== null && { min_sharp: parseFloat(applied.min_sharp) }),
    ...(applied.apply_status && { apply_status: applied.apply_status }),
    ...(applied.provider && { provider: applied.provider.trim() }),
    ...(applied.game_status && { game_status: applied.game_status.trim() }),
    ...(afterTs != null && { fixture_start_after: afterTs }),
    ...(beforeTs != null && { fixture_start_before: beforeTs }),
  };
}

const EXPOSURE_SORT_OPTIONS = [
  { value: 'fee_total', label: 'Total fees' },
  { value: 'fee_over', label: 'Over fees' },
  { value: 'fee_under', label: 'Under fees' },
  { value: 'fee_straight', label: 'Single-leg fees' },
  { value: 'vulnerability_score', label: 'Vulnerability score (Σ)' },
  { value: 'liability_net_over', label: 'Liability (over)' },
  { value: 'liability_net_under', label: 'Liability (under)' },
  { value: 'avg_sharp', label: 'Avg sharp' },
  { value: 'n_entries', label: 'Row count' },
  { value: 'outcome_value', label: 'Line' },
  { value: 'market_id', label: 'Market (A–Z)' },
  { value: 'player_name', label: 'Player (A–Z)' },
  { value: 'fixture_label', label: 'Fixture (A–Z)' },
];

function exposureGroupKey(row) {
  return `${row.market_id ?? ''}\t${row.player_name ?? ''}\t${row.fixture_label ?? ''}`;
}

function compareRowsByOutcomeValue(a, b) {
  const fa = a?.outcome_value != null && a.outcome_value !== '' ? Number(a.outcome_value) : NaN;
  const fb = b?.outcome_value != null && b.outcome_value !== '' ? Number(b.outcome_value) : NaN;
  if (!Number.isFinite(fa) && !Number.isFinite(fb)) return 0;
  if (!Number.isFinite(fa)) return 1;
  if (!Number.isFinite(fb)) return -1;
  return fa - fb;
}

function groupSortScalar(rows, metric) {
  if (metric === 'avg_sharp') {
    let w = 0;
    let s = 0;
    for (const r of rows) {
      const n = Number(r.n_entries) || 0;
      const sh = r.avg_sharp;
      if (sh != null && Number.isFinite(Number(sh)) && n > 0) {
        s += Number(sh) * n;
        w += n;
      }
    }
    if (w <= 0) return null;
    return s / w;
  }
  if (metric === 'market_id') {
    const t = rows[0]?.market_id;
    return t != null && String(t).trim() !== '' ? String(t).toLowerCase() : '';
  }
  if (metric === 'player_name') {
    const t = rows[0]?.player_name;
    return t != null && String(t).trim() !== '' ? String(t).toLowerCase() : '';
  }
  if (metric === 'fixture_label') {
    const t = rows[0]?.fixture_label;
    return t != null && String(t).trim() !== '' ? String(t).toLowerCase() : '';
  }
  if (metric === 'outcome_value') {
    let best = null;
    for (const r of rows) {
      const v = r.outcome_value;
      const n = v != null && v !== '' ? Number(v) : NaN;
      if (Number.isFinite(n)) {
        best = best == null ? n : Math.min(best, n);
      }
    }
    return best;
  }
  let sum = 0;
  for (const r of rows) {
    const v = Number(r[metric]);
    if (Number.isFinite(v)) sum += v;
  }
  return sum;
}

function compareExposureGroups(a, b, metric, sortOrder) {
  const dir = sortOrder === 'asc' ? 1 : -1;
  const strMetrics = new Set(['market_id', 'player_name', 'fixture_label']);
  const sa = groupSortScalar(a.rows, metric);
  const sb = groupSortScalar(b.rows, metric);
  if (strMetrics.has(metric)) {
    const ca = sa == null ? '' : sa;
    const cb = sb == null ? '' : sb;
    const c = dir * String(ca).localeCompare(String(cb), undefined, { sensitivity: 'base' });
    if (c !== 0) return c;
    return a.key.localeCompare(b.key);
  }
  const na = sa == null ? null : Number(sa);
  const nb = sb == null ? null : Number(sb);
  if (na == null && nb == null) return a.key.localeCompare(b.key);
  if (na == null) return 1;
  if (nb == null) return -1;
  const c = (na - nb) * dir;
  if (c !== 0) return c;
  return a.key.localeCompare(b.key);
}

function formatExposureLine(v) {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(String(v).trim());
  if (!Number.isFinite(n)) return String(v);
  if (Number.isInteger(n) || Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
  return n.toFixed(2);
}

function formatMoney(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toFixed(2);
}

/** ``YYYY-MM-DD`` for ``<input type="date">`` (local calendar). */
function formatDateInputLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Default kickoff window: calendar yesterday → calendar tomorrow (local dates only). */
function defaultKickoffRangeLocal() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 5, 0, 0, 0, 0);
  return {
    fixture_start_after: formatDateInputLocal(from),
    fixture_start_before: formatDateInputLocal(to),
  };
}

/** Initial filters: ``EMPTY_ENTRY_FILTERS`` plus default kickoff dates (yesterday → tomorrow, local). */
const INITIAL_ENTRY_FILTERS = {
  ...EMPTY_ENTRY_FILTERS,
  ...defaultKickoffRangeLocal(),
};

/** Parse ``YYYY-MM-DD`` from ``<input type="date">``; invalid → ``undefined``. */
function parseDateInputYmd(dateStr) {
  const s = dateStr == null ? '' : String(dateStr).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return { y, mo, d };
}

/** Start of local calendar day → Unix seconds (for ``fixture_start_after`` / ``>=``). */
function fixtureKickoffAfterTsForApi(dateStr) {
  const p = parseDateInputYmd(dateStr);
  if (!p) return undefined;
  const t = new Date(p.y, p.mo, p.d, 0, 0, 0, 0).getTime();
  if (!Number.isFinite(t)) return undefined;
  return t / 1000;
}

/** End of local calendar day (inclusive for ``<=``). */
function fixtureKickoffBeforeTsForApi(dateStr) {
  const p = parseDateInputYmd(dateStr);
  if (!p) return undefined;
  const t = new Date(p.y, p.mo, p.d, 23, 59, 59, 999).getTime();
  if (!Number.isFinite(t)) return undefined;
  return t / 1000;
}

/** ``<= 0`` or invalid → API uses 7-day ``created_ts`` floor (partition-friendly; see server). */
function entriesMinutesForApi(minutes) {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return 7 * 24 * 60;
  return m;
}

const WINDOW_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '60 min' },
  { value: 120, label: '120 min' },
  { value: 7 * 24 * 60, label: '7 days' },
];

function windowLabel(minutes) {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return 'last 7 days';
  if (m >= 24 * 60) {
    const d = Math.round(m / (24 * 60));
    return d === 1 ? 'last 24 hours' : `last ${d} days`;
  }
  return `last ${m} min`;
}

function formatIsoShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
}

function formatCompositionLeg(leg) {
  const player = leg?.player_name || '—';
  const mkt = leg?.market_id || leg?.wager_type || '—';
  const side = leg?.outcome ? String(leg.outcome) : '';
  const line = leg?.outcome_value != null ? Number(leg.outcome_value) : '';
  return `${player} · ${mkt} ${side} ${line}`.replace(/\s+/g, ' ').trim();
}

function formatCompositionSummary(legs) {
  if (!Array.isArray(legs) || legs.length === 0) return '—';
  return legs.map(formatCompositionLeg).join(' | ');
}

function formatReplicationSport(sport) {
  const s = String(sport || '').trim().toLowerCase();
  if (!s) return '—';
  const labels = {
    soccer: 'Soccer',
    basketball: 'Basketball',
    nba: 'NBA',
    wnba: 'WNBA',
    mlb: 'MLB',
    golf: 'Golf',
    esports: 'Esports',
    cbb: 'CBB',
    ncaab: 'NCAAB',
    football: 'NFL',
    nfl: 'NFL',
    ncaaf: 'NCAAF / CFB',
    cfb: 'NCAAF / CFB',
  };
  return labels[s] || s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function compositionLegParts(leg) {
  return {
    player: leg?.player_name || '—',
    market: leg?.market_id || leg?.wager_type || '—',
    side: leg?.outcome ? String(leg.outcome) : '—',
    line: leg?.outcome_value != null ? Number(leg.outcome_value) : '—',
  };
}

/** Collapsed: caret + leg count; expanded: structured leg rows. */
function ReplicationCompositionCell({ legs, numLegs, expanded, onToggle }) {
  const list = Array.isArray(legs) ? legs : [];
  const n = numLegs != null ? Number(numLegs) : list.length;
  const label = Number.isFinite(n) && n > 0 ? `${n} leg${n === 1 ? '' : 's'}` : 'No legs';

  if (!list.length) {
    return <span className="entries-repl-composition-empty">—</span>;
  }

  return (
    <div className={`entries-repl-disclosure${expanded ? ' is-open' : ''}`}>
      <button
        type="button"
        className="entries-repl-disclosure-toggle"
        aria-expanded={expanded}
        onClick={onToggle}
        title={expanded ? 'Hide legs' : formatCompositionSummary(list)}
      >
        <span className="entries-repl-caret" aria-hidden="true" />
        <span className="entries-repl-disclosure-label">{label}</span>
      </button>
      {expanded ? (
        <ul className="entries-repl-composition-list">
          {list.map((leg, legIdx) => {
            const p = compositionLegParts(leg);
            return (
              <li key={legIdx}>
                <span className="entries-repl-leg-player">{p.player}</span>
                <span className="entries-repl-leg-meta">
                  {p.market}
                  <span className="entries-repl-leg-sep">·</span>
                  {p.side}
                  <span className="entries-repl-leg-sep">·</span>
                  {p.line}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function EntriesPage() {
  const navigate = useNavigate();
  const { tab } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const invalidTab = tab !== 'ticker' && tab !== 'exposure' && tab !== 'replication';
  /** URL: /entries/ticker | /entries/exposure | /entries/replication */
  const entriesView =
    tab === 'exposure' ? 'exposure' : tab === 'replication' ? 'replication' : 'ticker';
  const [minutes, setMinutes] = useState(60);
  const [limit, setLimit] = useState(200);
  const [sortBy, setSortBy] = useState('created_ts');
  const [sortOrder, setSortOrder] = useState('desc');
  const [exposureSortBy, setExposureSortBy] = useState('fee_total');
  const [exposureSortOrder, setExposureSortOrder] = useState('desc');
  const [exposureClosedFilter, setExposureClosedFilter] = useState('');
  const [replMinReg, setReplMinReg] = useState(100);
  const [replMinHf, setReplMinHf] = useState(3);
  const [replLimit, setReplLimit] = useState(50);
  /** Expanded composition hashes on the Replication tab. */
  const [expandedReplKeys, setExpandedReplKeys] = useState(() => new Set());
  /** Draft filter values (not sent until Search / Enter). */
  const [draftFilters, setDraftFilters] = useState(() => ({ ...INITIAL_ENTRY_FILTERS }));
  /** Snapshot passed to the API (updated only on Search / Enter). */
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...INITIAL_ENTRY_FILTERS }));
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const pid = (searchParams.get('parlay_id') || '').trim();
    if (!pid || entriesView !== 'ticker') return;
    setDraftFilters((prev) => ({ ...prev, parlay_id: pid }));
    setAppliedFilters((prev) => ({ ...prev, parlay_id: pid }));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('parlay_id');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, entriesView, setSearchParams]);

  const replParams = useMemo(
    () => ({
      min_n_reg: replMinReg,
      min_n_hf: replMinHf,
      limit: replLimit,
    }),
    [replMinReg, replMinHf, replLimit]
  );

  const queryParams = useMemo(
    () => buildEntriesQueryParams(minutes, limit, sortBy, sortOrder, appliedFilters),
    [minutes, limit, sortBy, sortOrder, appliedFilters]
  );

  const exposureParams = useMemo(
    () =>
      buildExposureQueryParams(
        minutes,
        limit,
        appliedFilters,
        exposureSortBy,
        exposureSortOrder
      ),
    [minutes, limit, appliedFilters, exposureSortBy, exposureSortOrder]
  );

  const { data: entries = [], isLoading } = useRecentEntries(queryParams, {
    enabled: entriesView === 'ticker' && !invalidTab,
  });
  const { data: exposureRows = [], isLoading: exposureLoading } = useEntriesExposure(exposureParams, {
    enabled: entriesView === 'exposure' && !invalidTab,
  });
  const { data: hotCompositions = [], isLoading: replLoading } = useHotCompositions(replParams, {
    enabled: entriesView === 'replication' && !invalidTab,
  });

  const filteredExposureRows = useMemo(
    () => exposureRows.filter((row) => exposureRowMatchesClosedFilter(row, exposureClosedFilter, nowMs)),
    [exposureRows, exposureClosedFilter, nowMs]
  );

  // Seeded options plus whatever the producer actually sent in the loaded ticker rows, so an
  // unexpected vocabulary stays filterable. Exposure rows are aggregates and carry no leg
  // status, hence ticker rows only.
  const gameStatusOptions = useMemo(
    () => gameStatusFilterOptions(entries, appliedFilters.game_status),
    [entries, appliedFilters.game_status]
  );

  const exposureGroups = useMemo(() => {
    if (!filteredExposureRows.length) return [];
    const byKey = new Map();
    for (const row of filteredExposureRows) {
      const key = exposureGroupKey(row);
      let g = byKey.get(key);
      if (!g) {
        g = { key, rows: [] };
        byKey.set(key, g);
      }
      g.rows.push(row);
    }
    const groups = Array.from(byKey.values());
    for (const g of groups) {
      g.rows.sort(compareRowsByOutcomeValue);
    }
    groups.sort((a, b) => compareExposureGroups(a, b, exposureSortBy, exposureSortOrder));
    return groups;
  }, [filteredExposureRows, exposureSortBy, exposureSortOrder]);

  const exposureLiabilityMax = useMemo(
    () => maxAbsLiabilityFromRows(filteredExposureRows, [], ['liability_net_over', 'liability_net_under']),
    [filteredExposureRows]
  );

  const tickerLiabilityMax = useMemo(
    () => maxAbsLiabilityFromRows(entries, [], ['liability_contrib']),
    [entries]
  );

  const applySearch = useCallback(() => {
    setAppliedFilters({ ...draftFilters });
  }, [draftFilters]);

  const handleFilterChange = useCallback((key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleFilterKeyDown = useCallback(
    (e) => {
      if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
      e.preventDefault();
      applySearch();
    },
    [applySearch]
  );

  const handleSort = useCallback((key) => {
    setSortBy(key);
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  }, []);

  const handleExposureSort = useCallback((key) => {
    setExposureSortBy(key);
    setExposureSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  }, []);

  const clearFilters = useCallback(() => {
    setDraftFilters({ ...EMPTY_ENTRY_FILTERS });
    setAppliedFilters({ ...EMPTY_ENTRY_FILTERS });
  }, []);

  const goToDashboard = useCallback(
    (marketKey) => {
      if (!marketKey || !String(marketKey).trim()) return;
      navigate(`/dashboard?market=${encodeURIComponent(marketKey)}`);
    },
    [navigate]
  );

  const goToTickerParlay = useCallback(
    (parlayId) => {
      if (!parlayId || !String(parlayId).trim()) return;
      navigate(`/entries/ticker?parlay_id=${encodeURIComponent(String(parlayId).trim())}`);
    },
    [navigate]
  );

  const toggleReplComposition = useCallback((key) => {
    if (!key) return;
    setExpandedReplKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const pageTitle =
    entriesView === 'exposure'
      ? 'Entries · Exposure'
      : entriesView === 'replication'
        ? 'Entries · Replication'
        : 'Entries · Ticker';

  if (invalidTab) {
    return <Navigate to="/entries/ticker" replace />;
  }

  return (
    <div className="entries-page">
      <div
        className={`page-header${entriesView === 'exposure' ? ' page-header--entries-exposure' : ''}`}
      >
        <h1>
          {pageTitle}
          {entriesView !== 'replication' ? ` (${windowLabel(minutes)})` : ''}
        </h1>
        <div className="header-actions">
          {entriesView === 'replication' && (
            <div className="entries-exposure-spec-row" aria-label="Replication thresholds">
              <label>
                Min reg
                <input
                  type="number"
                  min={0}
                  step={10}
                  value={replMinReg}
                  onChange={(e) => setReplMinReg(Number(e.target.value) || 0)}
                  className="filter-input entries-filter-input entries-repl-threshold"
                />
              </label>
              <label>
                Min hf
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={replMinHf}
                  onChange={(e) => setReplMinHf(Number(e.target.value) || 0)}
                  className="filter-input entries-filter-input entries-repl-threshold"
                />
              </label>
              <label>
                Limit
                <select
                  value={replLimit}
                  onChange={(e) => setReplLimit(Number(e.target.value))}
                  className="entries-select"
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
          )}
          {entriesView === 'exposure' && (
            <div className="entries-exposure-spec-row" aria-label="Exposure sort">
              <label>
                Sort
                <select
                  value={exposureSortBy}
                  onChange={(e) => setExposureSortBy(e.target.value)}
                  className="entries-select"
                >
                  {EXPOSURE_SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Order
                <select
                  value={exposureSortOrder}
                  onChange={(e) => setExposureSortOrder(e.target.value)}
                  className="entries-select"
                >
                  <option value="desc">High → low</option>
                  <option value="asc">Low → high</option>
                </select>
              </label>
            </div>
          )}
          <label>
            Window
            <select
              value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
              className="entries-select"
              title="Rolling window on entry created time. “7 days” caps history for cheaper partitioned queries. Same for Ticker and Exposure."
              disabled={entriesView === 'replication'}
            >
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Limit
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="entries-select"
              disabled={entriesView === 'replication'}
            >
              {[100, 200, 500, 1000].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {entriesView !== 'replication' && (
      <div
        className="card filter-bar entries-filter-bar"
        title="Enter in a field or click Search. Selects apply with Search."
      >
        <div className="filter-row entries-filter-row">
          <label className="entries-filter-label" title="Display name or player segment of market_key.">
            Player
            <input
              type="text"
              value={draftFilters.player}
              onChange={(e) => handleFilterChange('player', e.target.value)}
              onKeyDown={handleFilterKeyDown}
              placeholder="Contains…"
              className="filter-input entries-filter-input"
              autoComplete="off"
            />
          </label>
          <label className="entries-filter-label" title="market_id, key segment, or wager_type.">
            Market
            <input
              type="text"
              value={draftFilters.market}
              onChange={(e) => handleFilterChange('market', e.target.value)}
              onKeyDown={handleFilterKeyDown}
              placeholder="Contains…"
              className="filter-input entries-filter-input entries-filter-input-wide"
              autoComplete="off"
            />
          </label>
          {entriesView === 'exposure' && (
            <label className="entries-filter-label" title="Filter Exposure rows by whether the fixture is closed.">
              Closed
              <select
                value={exposureClosedFilter}
                onChange={(e) => setExposureClosedFilter(e.target.value)}
                className="filter-select entries-filter-select entries-filter-select-closed"
              >
                <option value="">All games</option>
                <option value="closed">Closed only</option>
                <option value="open">Open only</option>
              </select>
            </label>
          )}
          <label className="entries-filter-label" title="Leg provider (exact).">
            Provider
            <select
              value={draftFilters.provider}
              onChange={(e) => handleFilterChange('provider', e.target.value)}
              className="filter-select entries-filter-select entries-filter-select-provider"
            >
              {PROVIDER_FILTER_OPTIONS.map((o) => (
                <option key={o.value || 'all-providers'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label
            className="entries-filter-label"
            title="Whether the leg was taken pre-game or in-game, as the producer recorded it at placement (line.game_status)."
          >
            Game status
            <select
              value={draftFilters.game_status}
              onChange={(e) => handleFilterChange('game_status', e.target.value)}
              className="filter-select entries-filter-select entries-filter-select-game-status"
            >
              {gameStatusOptions.map((o) => (
                <option key={o.value || 'all-game-status'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="entries-filter-label" title="Sleeper / internal user id.">
            User ID
            <input
              type="text"
              value={draftFilters.user_id}
              onChange={(e) => handleFilterChange('user_id', e.target.value)}
              onKeyDown={handleFilterKeyDown}
              placeholder="Exact…"
              className="filter-input entries-filter-input entries-filter-input-user"
              autoComplete="off"
            />
          </label>
          <label className="entries-filter-label" title="Over / under leg filter.">
            Outcome
            <select
              value={draftFilters.outcome}
              onChange={(e) => handleFilterChange('outcome', e.target.value)}
              className="filter-select entries-filter-select entries-filter-select-outcome"
            >
              <option value="">Any</option>
              <option value="over">Over</option>
              <option value="under">Under</option>
            </select>
          </label>
          <label className="entries-filter-label" title="Default hides unmanaged legs.">
            Apply
            <select
              value={draftFilters.apply_status}
              onChange={(e) => handleFilterChange('apply_status', e.target.value)}
              className="filter-select entries-filter-select entries-filter-select-apply"
            >
              <option value="">Default (hide unmanaged)</option>
              <option value="all">All statuses</option>
              <option value="unmanaged">Unmanaged only</option>
              <option value="applied">Applied</option>
              <option value="skipped">Skipped</option>
              <option value="pending">Pending</option>
            </select>
          </label>
          <label className="entries-filter-label" title="LIST-partition sport.">
            Sport
            <select
              value={draftFilters.sport}
              onChange={(e) => handleFilterChange('sport', e.target.value)}
              className="filter-select entries-filter-select entries-filter-select-sport"
            >
              {SPORT_FILTER_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="entries-filter-label" title="Fixture kickoff on or after this date (local day).">
            Start Date from
            <input
              type="date"
              value={draftFilters.fixture_start_after}
              onChange={(e) => handleFilterChange('fixture_start_after', e.target.value)}
              className="filter-input entries-filter-date"
            />
          </label>
          <label className="entries-filter-label" title="Fixture kickoff on or before this date (local day).">
            Start Date to
            <input
              type="date"
              value={draftFilters.fixture_start_before}
              onChange={(e) => handleFilterChange('fixture_start_before', e.target.value)}
              className="filter-input entries-filter-date"
            />
          </label>
          <label className="entries-filter-label" title="Minimum sharp_score on the leg.">
            Min sharp
            <input
              type="number"
              value={draftFilters.min_sharp}
              onChange={(e) => handleFilterChange('min_sharp', e.target.value)}
              onKeyDown={handleFilterKeyDown}
              placeholder="—"
              step="0.01"
              className="filter-input entries-filter-input-num"
            />
          </label>
          <label className="entries-filter-label" title="Filter to one parlay ticket.">
            Parlay ID
            <input
              type="text"
              value={draftFilters.parlay_id}
              onChange={(e) => handleFilterChange('parlay_id', e.target.value)}
              onKeyDown={handleFilterKeyDown}
              placeholder="Exact…"
              className="filter-input entries-filter-input entries-filter-input-user"
              autoComplete="off"
            />
          </label>
          <label className="entries-filter-label" title="Minimum leg fee (currency amount).">
            Min fee
            <input
              type="number"
              value={draftFilters.min_fee}
              onChange={(e) => handleFilterChange('min_fee', e.target.value)}
              onKeyDown={handleFilterKeyDown}
              placeholder="—"
              min="0"
              step="0.01"
              className="filter-input entries-filter-input-num"
            />
          </label>
          <button type="button" className="button button-small" onClick={applySearch}>
            Search
          </button>
          <button type="button" className="button button-small" onClick={clearFilters}>
            Clear
          </button>
        </div>
      </div>
      )}

      <div className="card entries-table-card">
        <div className="entries-table-wrap">
          {entriesView === 'replication' ? (
            replLoading && hotCompositions.length === 0 ? (
              <div className="loading">Loading hot compositions…</div>
            ) : (
              <>
                <p className="entries-repl-lede">
                  Hot compositions (≥ reg/hf thresholds) with replication activity in the last 24 hours;
                  leg detail from existing entry rows.
                </p>
                <table className="table entries-table entries-replication-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th title="Regular-fee pending slips">Reg</th>
                      <th title="High-fee pending slips">HF</th>
                      <th title="Ranking: n_reg + 10×n_hf (HF slips weighted 10×)">Score</th>
                      <th title="Distinct users with pending copies of this composition">Users</th>
                      <th title="Mean raw sharp_score across those users (one value per user)">Avg sharp</th>
                      <th title="Dominant sport on the sample parlay">Sport</th>
                      <th title="Expand to view sample parlay legs">Composition</th>
                      <th>Last updated</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {hotCompositions.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="empty-cell">
                          No hot compositions (try lowering min reg / min hf, or enable replication_sharp_enabled).
                        </td>
                      </tr>
                    ) : (
                      hotCompositions.map((row, idx) => {
                        const replKey = row.composition_hash || `idx-${idx}`;
                        const isOpen = expandedReplKeys.has(replKey);
                        return (
                        <tr key={replKey} className={isOpen ? 'entries-repl-row-open' : undefined}>
                          <td>{idx + 1}</td>
                          <td className="entries-repl-num">{row.n_reg ?? '—'}</td>
                          <td className="entries-repl-num">{row.n_hf ?? '—'}</td>
                          <td className="entries-repl-num">{row.score ?? '—'}</td>
                          <td className="entries-repl-num">{row.n_users ?? '—'}</td>
                          <td
                            className="entries-repl-num"
                            style={sharpCellStyle(row.avg_sharp_score)}
                          >
                            {row.avg_sharp_score != null ? Number(row.avg_sharp_score).toFixed(2) : '—'}
                          </td>
                          <td className="entries-repl-sport">
                            <span className="entries-repl-sport-badge" title={row.sample_sport || ''}>
                              {formatReplicationSport(row.sample_sport)}
                            </span>
                          </td>
                          <td className="entries-repl-composition">
                            <ReplicationCompositionCell
                              legs={row.sample_legs}
                              numLegs={row.sample_num_legs}
                              expanded={isOpen}
                              onToggle={() => toggleReplComposition(replKey)}
                            />
                          </td>
                          <td>{formatIsoShort(row.updated_at)}</td>
                          <td>
                            {row.sample_parlay_id ? (
                              <button
                                type="button"
                                className="button button-small"
                                onClick={() => goToTickerParlay(row.sample_parlay_id)}
                              >
                                Ticker
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </>
            )
          ) : entriesView === 'exposure' ? (
            exposureLoading && exposureRows.length === 0 ? (
              <div className="loading">Loading exposure…</div>
            ) : (
              <table className="table entries-table entries-exposure-table">
                <thead>
                  <tr>
                    <th title="Fixture, player, and market (one block per slice)">Slice</th>
                    <th title="Fixture date, start time, and live countdown">Start</th>
                    <th
                      className="sortable"
                      onClick={() => handleExposureSort('outcome_value')}
                      title="Sort API rows by line; table groups lines under each slice"
                    >
                      Line{' '}
                      {exposureSortBy === 'outcome_value' && (exposureSortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleExposureSort('fee_over')}
                      title="Click to change sort"
                    >
                      Fee (over){' '}
                      {exposureSortBy === 'fee_over' && (exposureSortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleExposureSort('fee_under')}
                    >
                      Fee (under){' '}
                      {exposureSortBy === 'fee_under' && (exposureSortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleExposureSort('fee_total')}
                    >
                      Fee (total){' '}
                      {exposureSortBy === 'fee_total' && (exposureSortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleExposureSort('fee_straight')}
                      title="Fees from 1-leg (straight) entries only"
                    >
                      Fee (1-leg){' '}
                      {exposureSortBy === 'fee_straight' && (exposureSortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleExposureSort('vulnerability_score')}
                      title="Σ √P × ((fee/num_legs)/500) over legs in this slice"
                    >
                      Vuln Σ{' '}
                      {exposureSortBy === 'vulnerability_score' && (exposureSortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleExposureSort('liability_net_over')}
                      title="Σ E[profit|over wins] − fees on under (same slice)"
                    >
                      Liability (over){' '}
                      {exposureSortBy === 'liability_net_over' && (exposureSortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleExposureSort('liability_net_under')}
                      title="Σ E[profit|under wins] − fees on over (same slice)"
                    >
                      Liability (under){' '}
                      {exposureSortBy === 'liability_net_under' && (exposureSortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleExposureSort('avg_sharp')}
                    >
                      Avg sharp{' '}
                      {exposureSortBy === 'avg_sharp' && (exposureSortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleExposureSort('n_entries')}
                    >
                      Rows{' '}
                      {exposureSortBy === 'n_entries' && (exposureSortOrder === 'desc' ? '↓' : '↑')}
                    </th>
                    <th># Over</th>
                    <th># Under</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExposureRows.length === 0 ? (
                    <tr>
                      <td colSpan={14} className="empty-cell">
                        No rows in this window (try a wider time range, raising the limit, or clearing filters).
                      </td>
                    </tr>
                  ) : (
                    exposureGroups.flatMap((g) =>
                      g.rows.map((row, ri) => (
                        <tr key={`${g.key}\t${row.outcome_value ?? 'null'}\t${ri}`}>
                          {ri === 0 ? (
                            <td rowSpan={g.rows.length} className="entries-exposure-slice">
                              {exposureRowCanOpenDashboard(row) ? (
                                <Link
                                  to={dashboardOrderBookHref(row.market_key, row.outcome_value)}
                                  className="entries-exposure-slice-link"
                                  title="Open main market order book"
                                >
                                  <div
                                    className="entries-exposure-slice-fixture"
                                    title={row.fixture_label != null ? String(row.fixture_label) : ''}
                                  >
                                    {row.fixture_label != null && row.fixture_label !== ''
                                      ? String(row.fixture_label)
                                      : '—'}
                                  </div>
                                  <div
                                    className="entries-exposure-slice-player"
                                    title={row.player_name != null ? String(row.player_name) : ''}
                                  >
                                    {row.player_name != null && row.player_name !== ''
                                      ? String(row.player_name)
                                      : '—'}
                                  </div>
                                  <div
                                    className="entries-exposure-slice-market"
                                    title={row.market_id != null ? String(row.market_id) : ''}
                                  >
                                    {row.market_id != null && row.market_id !== ''
                                      ? String(row.market_id)
                                      : '—'}
                                  </div>
                                </Link>
                              ) : (
                                <>
                                  <div
                                    className="entries-exposure-slice-fixture"
                                    title={row.fixture_label != null ? String(row.fixture_label) : ''}
                                  >
                                    {row.fixture_label != null && row.fixture_label !== ''
                                      ? String(row.fixture_label)
                                      : '—'}
                                  </div>
                                  <div
                                    className="entries-exposure-slice-player"
                                    title={row.player_name != null ? String(row.player_name) : ''}
                                  >
                                    {row.player_name != null && row.player_name !== ''
                                      ? String(row.player_name)
                                      : '—'}
                                  </div>
                                  <div
                                    className="entries-exposure-slice-market"
                                    title={row.market_id != null ? String(row.market_id) : ''}
                                  >
                                    {row.market_id != null && row.market_id !== ''
                                      ? String(row.market_id)
                                      : '—'}
                                  </div>
                                </>
                              )}
                            </td>
                          ) : null}
                          {ri === 0 ? (
                            <>
                              <td rowSpan={g.rows.length} className="entries-exposure-start">
                                <div className="entries-exposure-start-date">
                                  {formatEntryDate(row.fixture_start_ts)}
                                </div>
                                <div className="entries-exposure-start-time">
                                  {formatEntryStartTime(row.fixture_start_ts)}
                                </div>
                                <div
                                  className={
                                    entryFixtureCompleted(row, nowMs)
                                      ? 'entries-exposure-start-status entries-exposure-start-status-complete'
                                      : 'entries-exposure-start-status'
                                  }
                                >
                                  {formatTimeToStart(row, nowMs)}
                                </div>
                              </td>
                            </>
                          ) : null}
                          <td className="entries-exposure-line">{formatExposureLine(row.outcome_value)}</td>
                          <td>{formatMoney(row.fee_over)}</td>
                          <td>{formatMoney(row.fee_under)}</td>
                          <td>{formatMoney(row.fee_total)}</td>
                          <td title="Fees from 1-leg entries only">{formatMoney(row.fee_straight)}</td>
                          <td title="Σ √P × ((fee/num_legs)/500)">
                            {row.vulnerability_score != null && Number.isFinite(Number(row.vulnerability_score))
                              ? Number(row.vulnerability_score).toFixed(4)
                              : '—'}
                          </td>
                          <td>
                            <span className={`entries-li-value ${liabilityCellClasses(row.liability_net_over, exposureLiabilityMax)}`}>
                              {formatMoney(row.liability_net_over)}
                            </span>
                          </td>
                          <td>
                            <span className={`entries-li-value ${liabilityCellClasses(row.liability_net_under, exposureLiabilityMax)}`}>
                              {formatMoney(row.liability_net_under)}
                            </span>
                          </td>
                          <td
                            className="entries-sharp-cell"
                            style={
                              getSharpColor(row.avg_sharp)
                                ? {
                                    backgroundColor: getSharpColor(row.avg_sharp),
                                    color: getSharpTextColor(row.avg_sharp),
                                  }
                                : undefined
                            }
                          >
                            {row.avg_sharp != null ? Number(row.avg_sharp).toFixed(3) : '—'}
                          </td>
                          <td>{row.n_entries != null ? row.n_entries : '—'}</td>
                          <td>{row.n_over != null ? row.n_over : '—'}</td>
                          <td>{row.n_under != null ? row.n_under : '—'}</td>
                        </tr>
                      ))
                    )
                  )}
                </tbody>
              </table>
            )
          ) : isLoading && entries.length === 0 ? (
            <div className="loading">Loading entries...</div>
          ) : (
            <table className="table entries-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('created_ts')} className="sortable">
                    Time {sortBy === 'created_ts' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th>Player</th>
                  <th>Market</th>
                  <th>Fixture</th>
                  <th onClick={() => handleSort('outcome')} className="sortable">
                    Outcome {sortBy === 'outcome' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('outcome_value')} className="sortable">
                    Line {sortBy === 'outcome_value' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('currency_amount')} className="sortable">
                    Fee {sortBy === 'currency_amount' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th title="√P × ((fee/num_legs)/500) using current market_quotes.P">
                    Vuln
                  </th>
                  <th onClick={() => handleSort('num_legs')} className="sortable" title="Total legs on the entry (1 = straight, 2+ = parlay)">
                    Num legs {sortBy === 'num_legs' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('sharp_score')} className="sortable">
                    Sharp (raw) {sortBy === 'sharp_score' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('sharp_score_adjusted')} className="sortable">
                    Sharp (adj) {sortBy === 'sharp_score_adjusted' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('replication_n_reg')} className="sortable" title="Replication count when hot (100+ reg or 3+ hf)">
                    Repl {sortBy === 'replication_n_reg' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('payout_multiplier')} className="sortable" title="Decimal odds for this leg">
                    Leg × {sortBy === 'payout_multiplier' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('entry_multiplier')} className="sortable" title="Full parlay decimal odds (Pub/Sub profit-on-stake + 1 at ingest)">
                    Entry × {sortBy === 'entry_multiplier' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('liability_contrib')} className="sortable" title="E[profit payout | this leg wins] component">
                    Liab. {sortBy === 'liability_contrib' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('probability')} className="sortable">
                    Prob {sortBy === 'probability' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th onClick={() => handleSort('provider')} className="sortable">
                    Provider {sortBy === 'provider' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th
                    onClick={() => handleSort('game_status')}
                    className="sortable"
                    title="Pre-game vs in-game at placement (producer's line.game_status). Blank = not stored."
                  >
                    Game status {sortBy === 'game_status' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th>User</th>
                  <th>Parlay</th>
                  <th>Apply status</th>
                  <th>Parlay status</th>
                  <th>Sport</th>
                  <th>Promo</th>
                  <th>Type</th>
                  <th onClick={() => handleSort('line_type')} className="sortable">
                    Line type {sortBy === 'line_type' && (sortOrder === 'desc' ? '↓' : '↑')}
                  </th>
                  <th>Profit boost %</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan="28" className="empty-cell">
                      No entries in {windowLabel(minutes)}.
                    </td>
                  </tr>
                ) : (
                  entries.map((row) => {
                    const rowTooltip = getEntryRowTooltip(row);
                    const canOpenDashboard = entryRowCanOpenDashboard(row);
                    const promoClass = entryPromoRowClass(row);
                    const replBadge = formatReplicationBadge(row);
                    return (
                      <tr
                        key={row.id != null ? row.id : `${row.parlay_id}-${row.market_key}-${row.outcome}-${row.outcome_value}`}
                        onClick={() => canOpenDashboard && goToDashboard(row.market_key)}
                        className={[canOpenDashboard ? 'clickable-row' : null, promoClass].filter(Boolean).join(' ') || undefined}
                        style={canOpenDashboard ? undefined : { cursor: 'default' }}
                      >
                        <td title={rowTooltip}>{formatTs(row.created_ts)}</td>
                        <td title={rowTooltip}>{row.player_name || '—'}</td>
                        <td title={rowTooltip}>{row.market_id || '—'}</td>
                        <td title={rowTooltip}>{formatFixture(row)}</td>
                        <td title={rowTooltip}>{row.outcome || '—'}</td>
                        <td title={rowTooltip}>{row.outcome_value != null ? row.outcome_value : '—'}</td>
                        <td title={rowTooltip}>{row.currency_amount != null ? row.currency_amount.toFixed(2) : '—'}</td>
                        <td
                          title={
                            row.market_p != null
                              ? `√P=${Math.sqrt(Math.max(Number(row.market_p), 0)).toPrecision(3)} × ((fee/num_legs)/500)`
                              : '√P × ((fee/num_legs)/500)'
                          }
                        >
                          {row.vulnerability_score != null && Number.isFinite(Number(row.vulnerability_score))
                            ? Number(row.vulnerability_score).toFixed(4)
                            : '—'}
                        </td>
                        <td title={rowTooltip}>{row.num_legs != null ? row.num_legs : '—'}</td>
                        <td
                          className="entries-sharp-cell"
                          title={rowTooltip}
                          style={sharpCellStyle(row.sharp_score)}
                        >
                          {row.sharp_score != null ? row.sharp_score.toFixed(2) : '—'}
                        </td>
                        <td
                          className="entries-sharp-cell"
                          title={rowTooltip}
                          style={sharpCellStyle(row.sharp_score_adjusted)}
                        >
                          {row.sharp_score_adjusted != null ? (
                            <>
                              {Number(row.sharp_score_adjusted).toFixed(2)}
                              {row.sharp_score != null
                                && Math.abs(Number(row.sharp_score_adjusted) - Number(row.sharp_score)) > 0.005
                                ? ' ↑'
                                : null}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="entries-repl-cell" title={rowTooltip}>
                          {replBadge || '—'}
                        </td>
                        <td title={rowTooltip}>{row.payout_multiplier != null ? row.payout_multiplier.toFixed(2) : '—'}</td>
                        <td title={rowTooltip}>{row.entry_multiplier != null ? Number(row.entry_multiplier).toFixed(2) : '—'}</td>
                        <td title={rowTooltip}>
                          {row.liability_contrib != null ? (
                            <span className={`entries-li-value ${liabilityCellClasses(row.liability_contrib, tickerLiabilityMax)}`}>
                              {formatMoney(row.liability_contrib)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td title={rowTooltip}>{row.probability != null ? (row.probability * 100).toFixed(1) + '%' : '—'}</td>
                        <td title={rowTooltip}>{row.provider != null && String(row.provider).trim() !== '' ? String(row.provider) : '—'}</td>
                        <td title={rowTooltip}>{row.game_status != null && String(row.game_status).trim() !== '' ? String(row.game_status) : '—'}</td>
                        <td title={rowTooltip}>{row.user_id ?? '—'}</td>
                        <td title={rowTooltip}>{row.parlay_id ?? '—'}</td>
                        <td title={rowTooltip}>
                          <span
                            className={`entries-status-pill ${entryStatusClass(row.application_status)}`}
                          >
                            {row.application_status || '—'}
                          </span>
                        </td>
                        <td title={rowTooltip}>
                          <span
                            className={`entries-status-pill entries-parlay-pill ${parlayStatusClass(row.parlay_status)}`}
                          >
                            {row.parlay_status || '—'}
                          </span>
                        </td>
                        <td title={rowTooltip}>{row.sport || '—'}</td>
                        <td title={rowTooltip}>{row.promo_type || '—'}</td>
                        <td title={rowTooltip}>{row.type || '—'}</td>
                        <td title={rowTooltip}>{row.line_type || '—'}</td>
                        <td title={rowTooltip}>{row.profit_boost_percentage != null ? row.profit_boost_percentage*100 + '%' : '—'}</td>
                        <td title={rowTooltip} onClick={(e) => e.stopPropagation()}>
                          {canOpenDashboard ? (
                            <a
                              href={`/dashboard?market=${encodeURIComponent(row.market_key)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="entries-link"
                            >
                              Open
                            </a>
                          ) : (
                            <span
                              className="entries-link entries-link--disabled"
                              title={
                                row.market_key && String(row.market_key).trim()
                                  ? 'No quoted market on this service for this key (persist-only / no engine row)'
                                  : undefined
                              }
                            >
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
        {!isLoading && entriesView === 'ticker' && entries.length > 0 && (
          <div className="entries-footer">
            {entries.length} entries
          </div>
        )}
        {!exposureLoading && entriesView === 'exposure' && filteredExposureRows.length > 0 && (
          <div className="entries-footer">
            {exposureGroups.length} slice{exposureGroups.length === 1 ? '' : 's'} · {filteredExposureRows.length} line-level
            row{filteredExposureRows.length === 1 ? '' : 's'} (market × player × fixture × line)
          </div>
        )}
        {!replLoading && entriesView === 'replication' && hotCompositions.length > 0 && (
          <div className="entries-footer">
            {hotCompositions.length} hot composition{hotCompositions.length === 1 ? '' : 's'} · min reg {replMinReg} / min hf {replMinHf}
          </div>
        )}
      </div>
    </div>
  );
}

export default EntriesPage;
